import React from 'react';
import { Button } from './ui/button';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  maxVisiblePages = 7 
}) => {
  // Calculate which page numbers to show
  const getVisiblePages = () => {
    const pages = [];
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate start and end pages
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = startPage + maxVisiblePages - 1;
      
      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
      
      // Add visible pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis and last page if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const visiblePages = getVisiblePages();

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="flex justify-center items-center space-x-1 mt-12" data-testid="pagination">
      
      {/* Previous Button */}
      <Button
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        variant="ghost"
        className="px-3 py-2 text-gray-400 hover:text-orange-400 hover:bg-orange-600/10 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="pagination-prev"
      >
        ‹ Prev
      </Button>

      {/* Page Numbers */}
      <div className="flex items-center space-x-1 mx-4">
        {visiblePages.map((page, index) => {
          if (page === '...') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="px-3 py-2 text-gray-500"
              >
                ...
              </span>
            );
          }

          const isCurrentPage = page === currentPage;
          
          return (
            <Button
              key={page}
              onClick={() => onPageChange(page)}
              className={`px-3 py-2 min-w-[40px] ${
                isCurrentPage
                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                  : 'text-gray-400 hover:text-orange-400 hover:bg-orange-600/10'
              } transition-all duration-200`}
              variant={isCurrentPage ? 'default' : 'ghost'}
              data-testid={`pagination-page-${page}`}
            >
              {page}
            </Button>
          );
        })}
      </div>

      {/* Next Button */}
      <Button
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        variant="ghost"
        className="px-3 py-2 text-gray-400 hover:text-orange-400 hover:bg-orange-600/10 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="pagination-next"
      >
        Next ›
      </Button>

      {/* Page Info */}
      <div className="ml-6 text-sm text-gray-500">
        Page {currentPage} of {totalPages}
      </div>
    </div>
  );
};

export default Pagination;